import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import { GALLERY_IMAGES } from "@/data/eventData";

const PLACEHOLDER_CHAPTERS = [
    { numeral: "I", label: "Chapter 1" },
    { numeral: "II", label: "Chapter 2" },
    { numeral: "III", label: "Chapter 3" },
];

const EmptyFrame = ({ numeral, label }) => (
    <div className="group relative aspect-[4/5] w-[70vw] shrink-0 snap-center overflow-hidden border border-cream/10 sm:w-[300px] md:w-auto">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_50%_100%,rgba(201,31,38,0.18),transparent_65%)] transition-opacity duration-500 group-hover:opacity-150" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="font-serif text-7xl font-light italic text-cream/15 transition-colors duration-500 group-hover:text-scarlet/40 md:text-8xl">
                {numeral}
            </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 to-transparent p-5">
            <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-scarlet">
                {label}
            </p>
            <p className="mt-1.5 text-xs font-light text-ash">
                Photographs arriving soon
            </p>
        </div>
    </div>
);

const PhotoFrame = ({ photo, index, onOpen }) => (
    <button
        type="button"
        onClick={onOpen}
        data-testid={`gallery-photo-${index}`}
        aria-label={`Open photo: ${photo.caption}`}
        className="group relative aspect-[4/5] w-[70vw] shrink-0 snap-center overflow-hidden border border-cream/10 text-left sm:w-[300px] md:w-auto"
    >
        <img
            src={photo.src}
            alt={photo.caption}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                photo.focus || ""
            }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
        <div className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-cream/20 bg-ink/50 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
            <Expand className="h-4 w-4 text-cream" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5">
            <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-scarlet">
                {photo.edition}
            </p>
            <p className="mt-1.5 text-sm font-light text-cream">{photo.caption}</p>
        </div>
    </button>
);

export const Lightbox = ({ photo, index, total, onClose, onPrev, onNext }) => {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") onNext();
            if (e.key === "ArrowLeft") onPrev();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose, onPrev, onNext]);

    return (
    <motion.div
        data-testid="gallery-lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-5 backdrop-blur-md"
        role="dialog"
        aria-modal="true"
        aria-label={`Photo viewer: ${photo.caption}`}
    >
        <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close photo viewer"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-cream/15 bg-ink/60 text-cream transition-colors duration-300 hover:border-crimson hover:text-scarlet"
        >
            <X className="h-5 w-5" />
        </button>
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onPrev();
            }}
            data-testid="lightbox-prev"
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-cream/15 bg-ink/60 text-cream transition-colors duration-300 hover:border-crimson hover:text-scarlet md:left-8"
        >
            <ChevronLeft className="h-5 w-5" />
        </button>
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onNext();
            }}
            data-testid="lightbox-next"
            aria-label="Next photo"
            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-cream/15 bg-ink/60 text-cream transition-colors duration-300 hover:border-crimson hover:text-scarlet md:right-8"
        >
            <ChevronRight className="h-5 w-5" />
        </button>
        <motion.figure
            key={photo.src}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-4xl"
        >
            <img
                src={photo.src}
                alt={photo.caption}
                className="max-h-[76vh] w-auto max-w-full border border-cream/10 object-contain"
            />
            <figcaption className="mt-4 flex items-baseline justify-between gap-6">
                <span>
                    <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-scarlet">
                        {photo.edition}
                    </span>
                    <span className="mt-1 block text-sm font-light text-cream">
                        {photo.caption}
                    </span>
                </span>
                <span className="shrink-0 font-sans text-[10px] tracking-[0.3em] text-ash/60">
                    {index + 1} / {total}
                </span>
            </figcaption>
        </motion.figure>
    </motion.div>
    );
};

const Gallery = () => {
    const hasPhotos = GALLERY_IMAGES.length > 0;
    const [active, setActive] = useState(null);

    return (
        <section id="previous-editions" data-testid="gallery-section" className="scroll-mt-24 bg-coal/50 py-24 md:py-36">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <Reveal>
                    <SectionLabel index="08" text="Previous Editions" />
                </Reveal>
                <div className="mt-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <Reveal delay={0.1}>
                        <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                            Echoes of Past
                            <br />
                            <em className="italic text-scarlet">Chapters.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-sm font-light leading-relaxed text-ash md:text-base">
                            Glimpses from previous editions of I-ACE — the debates, the
                            gavels and the delegates who came before.
                        </p>
                    </Reveal>
                </div>
            </div>

            <div className="no-scrollbar mx-auto mt-14 flex max-w-[1400px] snap-x snap-mandatory gap-4 overflow-x-auto px-5 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:px-10">
                {hasPhotos
                    ? GALLERY_IMAGES.map((photo, i) => (
                          <Reveal key={photo.src} delay={(i % 3) * 0.08} className="contents">
                              <PhotoFrame photo={photo} index={i} onOpen={() => setActive(i)} />
                          </Reveal>
                      ))
                    : PLACEHOLDER_CHAPTERS.map((chapter, i) => (
                          <Reveal key={chapter.numeral} delay={i * 0.08} className="contents">
                              <EmptyFrame numeral={chapter.numeral} label={chapter.label} />
                          </Reveal>
                      ))}
            </div>

            <div className="mx-auto mt-12 max-w-[1400px] px-5 md:px-10">
                <Reveal>
                    <Link
                        to="/gallery"
                        data-testid="view-photo-archive-btn"
                        className="group inline-flex w-full items-center justify-center gap-3 border border-cream/15 px-8 py-4 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:border-crimson hover:bg-maroon/50 sm:w-auto"
                    >
                        View Photo Archive
                        <ArrowRight className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover:translate-x-1.5" />
                    </Link>
                </Reveal>
            </div>

            <AnimatePresence>
                {active !== null && GALLERY_IMAGES[active] && (
                    <Lightbox
                        photo={GALLERY_IMAGES[active]}
                        index={active}
                        total={GALLERY_IMAGES.length}
                        onClose={() => setActive(null)}
                        onPrev={() =>
                            setActive(
                                (a) => (a - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length
                            )
                        }
                        onNext={() => setActive((a) => (a + 1) % GALLERY_IMAGES.length)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
